/**
 * One face of the anatomical figure.
 *
 * The body frame is drawn first and filled, so unengaged muscles read as part
 * of a body rather than shapes floating in space. Everything above it arrives
 * pre-sorted into paint order — SVG has no z-index, so the order these are
 * emitted in *is* the stacking.
 *
 * The surface ramp is deliberately tight: the card sits at `contrast-700`, the
 * frame one step up at `contrast-600`, an unlit muscle one step above that at
 * `contrast-500`. That monotonic climb is what makes muscles read as sitting on
 * the body; widening the gaps would turn an anatomical drawing into a diagram.
 */

import {
  BASE_SHAPES,
  MUSCLE_MAP_VIEWBOX,
  buildMuscleMapGroups,
} from '@personal-trainer-app/shared/src/utils/muscle-map';

import type { MuscleFigureProps, MuscleHighlight } from './MuscleMap.types';
import type {
  MuscleMapPath,
  MuscleRole,
} from '@personal-trainer-app/shared/src/utils/muscle-map';

/** Accent carries one meaning here — how hard this exercise works the muscle. */
const FILL_BY_ROLE: Record<MuscleRole, string> = {
  primary: 'fill-primary-400',
  secondary: 'fill-primary-400/30',
  none: 'fill-contrast-500',
};

/**
 * Kept apart from the fill, because a selected muscle must swap its stroke and
 * exactly one `stroke-*` class may reach the element. Two of them do not
 * resolve by class order — Tailwind emits both rules at equal specificity, so
 * whichever lands later in the stylesheet wins and the selection ring silently
 * renders in the base colour.
 */
const STROKE_BY_ROLE: Record<MuscleRole, string> = {
  primary: 'stroke-primary-200',
  secondary: 'stroke-primary-300/65',
  none: 'stroke-stroke-default',
};

/**
 * The status palette, used when the caller passes `highlights`. Accent means
 * nothing here — sore is the one semantic colour (it is the user's own report,
 * and the one thing on the figure that asks for a decision today), trained
 * hard is a neutral emphasis, and a muscle merely trained lifts one step off
 * the base. Same rule as above: one `stroke-*` class per element.
 */
const FILL_BY_HIGHLIGHT: Record<MuscleHighlight | 'none', string> = {
  sore: 'fill-warning-400/55',
  trained_hard: 'fill-neutral-200/75',
  trained: 'fill-neutral-50/15',
  none: 'fill-contrast-500',
};

const STROKE_BY_HIGHLIGHT: Record<MuscleHighlight | 'none', string> = {
  sore: 'stroke-warning-400/70',
  trained_hard: 'stroke-neutral-50/40',
  trained: 'stroke-stroke-default',
  none: 'stroke-stroke-default',
};

function transformOf(shape: MuscleMapPath): string | undefined {
  if (!shape.rotate) return undefined;
  const [degrees, cx, cy] = shape.rotate;
  return `rotate(${degrees} ${cx} ${cy})`;
}

export function MuscleFigure({
  face,
  model,
  selectedId,
  interactive,
  onSelect,
  title,
  highlights,
}: MuscleFigureProps): React.JSX.Element {
  const groups = buildMuscleMapGroups(face, model, selectedId);

  // Status paint replaces role paint rather than layering on it: a muscle that
  // is both "primary mover" and "sore" would otherwise get two fills, and the
  // caller who asked for status has no exercise in mind.
  const paint = (
    group: (typeof groups)[number]
  ): { fill: string; stroke: string; width: number } => {
    if (highlights) {
      const status = highlights[group.muscleId] ?? 'none';
      return {
        fill: FILL_BY_HIGHLIGHT[status],
        stroke: STROKE_BY_HIGHLIGHT[status],
        width: status === 'sore' || status === 'trained_hard' ? 1.2 : 1,
      };
    }
    return {
      fill: FILL_BY_ROLE[group.role],
      stroke: STROKE_BY_ROLE[group.role],
      width: group.role === 'primary' ? 1.2 : 1,
    };
  };

  return (
    <svg
      viewBox={`0 0 ${MUSCLE_MAP_VIEWBOX.width} ${MUSCLE_MAP_VIEWBOX.height}`}
      className="h-auto w-full overflow-visible"
      role="img"
    >
      <title>{title}</title>

      <g className="fill-contrast-600 stroke-stroke-subtle" strokeWidth={1}>
        {BASE_SHAPES.map((shape, index) => (
          <path
            key={`frame-${index}`}
            d={shape.d}
            transform={
              shape.rotate
                ? `rotate(${shape.rotate[0]} ${shape.rotate[1]} ${shape.rotate[2]})`
                : undefined
            }
          />
        ))}
      </g>

      {groups.map((group) => {
        const { fill, stroke, width } = paint(group);
        return (
          <g
            key={group.muscleId}
            data-muscle={group.muscleId}
            data-highlight={
              highlights ? (highlights[group.muscleId] ?? 'none') : undefined
            }
            className={[
              fill,
              group.isSelected ? 'stroke-neutral-50' : stroke,
              // The browser's default ring traces the group's bounding box, which
              // for a paired muscle is a rectangle spanning the gap between left
              // and right — it points at the wrong thing. Focus is shown by
              // thickening the muscle's own outline instead, which is both
              // shape-accurate and the same language selection already speaks.
              interactive &&
                'cursor-pointer outline-none transition-[fill] duration-200 focus-visible:stroke-primary-300 focus-visible:stroke-2',
            ]
              .filter(Boolean)
              .join(' ')}
            strokeWidth={group.isSelected ? 2.6 : width}
            onClick={interactive ? () => onSelect(group.muscleId) : undefined}
            onKeyDown={
              interactive
                ? (event) => {
                    if (event.key !== 'Enter' && event.key !== ' ') return;
                    event.preventDefault();
                    onSelect(group.muscleId);
                  }
                : undefined
            }
            role={interactive ? 'button' : undefined}
            tabIndex={interactive ? 0 : undefined}
            aria-pressed={interactive ? group.isSelected : undefined}
            aria-label={interactive ? group.label : undefined}
          >
            {group.shapes.map((shape) => (
              <path
                key={shape.key}
                d={shape.d}
                transform={transformOf(shape)}
              />
            ))}
          </g>
        );
      })}
    </svg>
  );
}
